/** One open period on a weekday, local time. Day: 0 = Sunday ... 6 = Saturday. Times are HH:mm (24:00 = end of day). */
export interface WorkingPeriod {
    day: number;
    start: string;
    end: string;
}

/** A tenant's working hours for room analytics. */
export interface WorkingHours {
    /** IANA time zone, e.g. Europe/London. */
    timeZone: string;
    days: WorkingPeriod[];
}

/** GET/PUT/DELETE /api/namespaces/{id}/working-hours. `workingHours` is null when every hour counts. */
export interface WorkingHoursSettings {
    workingHours: WorkingHours | null;
    changedUtc: string | null;
    changedBy: string | null;
}

/** Where a resolved setting came from: the tenant, a location's override, or nowhere. */
export interface WorkingHoursSource {
    kind: 'tenant' | 'location' | 'none';
    locationId?: string | null;
    name?: string | null;
}

/** Working hours as reported on a metrics query: `applied` is false when the caller asked for every hour. */
export interface MetricWorkingHours extends WorkingHours {
    applied: boolean;
    hoursFrom?: WorkingHoursSource | null;
    timeZoneFrom?: WorkingHoursSource | null;
}

/** Hours in effect for a location. `days` null means every hour counts. */
export interface ResolvedWorkingHours {
    timeZone: string;
    days: WorkingPeriod[] | null;
    hoursFrom: WorkingHoursSource;
    timeZoneFrom: WorkingHoursSource;
}

/** A location's own settings; null parts are inherited from its parents, then the tenant. */
export interface LocationWorkingHoursOverride {
    timeZone: string | null;
    days: WorkingPeriod[] | null;
    allHours: boolean;
    changedUtc?: string | null;
    changedBy?: string | null;
}

/** GET/PUT/DELETE /api/locations/{id}/working-hours. */
export interface LocationWorkingHoursResponse {
    override: LocationWorkingHoursOverride | null;
    /** What the location would have without its own override. */
    inherited: ResolvedWorkingHours;
    effective: ResolvedWorkingHours;
}

/** Body of PUT /api/locations/{id}/working-hours. */
export interface LocationWorkingHoursUpdate {
    timeZone: string | null;
    days: WorkingPeriod[] | null;
    allHours: boolean;
}

/** Monday-first display order of weekdays (Date.getDay numbering). */
export const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0] as const;

/** The usual office week, used when working hours are first switched on. */
export function defaultWorkingHours(timeZone: string): WorkingHours {
    return { timeZone, days: [1, 2, 3, 4, 5].map((day) => ({ day, start: '08:00', end: '18:00' })) };
}

/** Short weekday names for a locale, indexed by Date.getDay() (0 = Sunday). */
export function weekdayNames(locale: string, style: 'short' | 'long' = 'short'): string[] {
    const format = new Intl.DateTimeFormat(locale, { weekday: style, timeZone: 'UTC' });
    // 2026-09-27 was a Sunday.
    return Array.from({ length: 7 }, (_, d) => format.format(new Date(Date.UTC(2026, 8, 27 + d))));
}

/**
 * Compact description, e.g. "Mon–Fri 08:00–18:00, Sat 09:00–12:00". Consecutive days (Monday first) with the same
 * hours are grouped into a range.
 */
export function describeWorkingHours(days: WorkingPeriod[], names: string[]): string {
    const hoursFor = (day: number) =>
        days
            .filter((p) => p.day === day)
            .sort((a, b) => a.start.localeCompare(b.start))
            .map((p) => `${p.start}–${p.end}`)
            .join(', ');
    const groups: { first: number; last: number; hours: string }[] = [];
    for (const day of WEEK_ORDER) {
        const hours = hoursFor(day);
        if (!hours) continue;
        const previous = groups[groups.length - 1];
        const adjacent = previous && WEEK_ORDER.indexOf(previous.last as (typeof WEEK_ORDER)[number]) === WEEK_ORDER.indexOf(day) - 1;
        if (previous && adjacent && previous.hours === hours) previous.last = day;
        else groups.push({ first: day, last: day, hours });
    }
    return groups.map((g) => `${g.first === g.last ? names[g.first] : `${names[g.first]}–${names[g.last]}`} ${g.hours}`).join(', ');
}

/** IANA time zones the browser knows, with the given zone included (so a saved zone always shows). */
export function timeZoneOptions(include?: string | null): string[] {
    const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
    const zones = intl.supportedValuesOf?.('timeZone') ?? ['UTC'];
    return include && !zones.includes(include) ? [include, ...zones] : zones;
}

/** The browser's own time zone, the default when working hours are first set. */
export function browserTimeZone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

/** One editable weekday: the portal edits a single open period per day. */
export interface WorkingDayRow {
    day: number;
    open: boolean;
    start: string;
    end: string;
}

/** Rows for every weekday, Monday first. A day with several stored periods shows its first opening and last closing. */
export function workingDayRows(hours: WorkingHours | null): WorkingDayRow[] {
    const source = hours ?? defaultWorkingHours('UTC');
    return WEEK_ORDER.map((day) => {
        const periods = source.days.filter((p) => p.day === day).sort((a, b) => a.start.localeCompare(b.start));
        return periods.length ? { day, open: true, start: periods[0].start, end: periods[periods.length - 1].end } : { day, open: false, start: '08:00', end: '18:00' };
    });
}

/** The request body for the rows, or an error key when they cannot be saved. */
export function workingHoursFromRows(timeZone: string, rows: WorkingDayRow[]): WorkingHours | 'workingHours.noDays' | 'workingHours.invalid' {
    const open = rows.filter((r) => r.open);
    if (open.length === 0) return 'workingHours.noDays';
    // "HH:mm" compares correctly as text; 00:00 as a closing time means midnight at the end of the day.
    const end = (r: WorkingDayRow) => (r.end === '00:00' ? '24:00' : r.end);
    if (open.some((r) => !r.start || !r.end || end(r) <= r.start)) return 'workingHours.invalid';
    return { timeZone, days: open.map((r) => ({ day: r.day, start: r.start, end: end(r) })) };
}
